import React, { useEffect, useState } from 'react';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import { useFormik } from 'formik';
import PatientBlock from 'components/molecules/patientBlock';
import { useTranslation } from 'react-i18next';
import BaseDatepicker from 'components/atoms/base-datepicker';
import GenderSelectBox from 'components/molecules/gender-select-box';
import RaceSelectBox from 'components/molecules/race-select-box';
import MaritalStatusSelectBox from 'components/molecules/marital-status-select-box';
import LanguageSelectBox from 'components/molecules/language-select-box';
import WorkingStatusSelectBox from 'components/molecules/working-status-select-box';
import TitleSelectBox from 'components/molecules/title-select-box';
import BaseFormItemGroup from 'components/atoms/base-form/item-group';
import BaseGrid from 'components/atoms/base-grid';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useEdit } from 'infrastructure/providers/edit-provider';
import { usePatientDevices } from 'infrastructure/hooks/device/use-patient-devices';
import { prepareArrToCopy } from 'infrastructure/functions/prepare-arr-to-copy';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import classNames from 'classnames';
import { isMobile } from 'utils/deviceHelper';
import useUser from 'utils/useUser';
import BaseTimePicker from 'components/atoms/base-timepicker';
import BaseInputNumber from 'components/atoms/base-input-number';
import InformationTooltip from 'components/atoms/information-tooltip';
import { timezonesDataSource } from 'infrastructure/data-sources/timezones';

import s from './styles.module.scss';
import { adminPanelPatientInfoValidationSchema } from './validation-schema';

import type { Patient } from 'infrastructure/classes/patient/patient';

const formKey = 'AdminPanelPatientInfoForm';

interface IAdminPanelPatientInfoFormProps {
  data: Patient;
  loading?: boolean;
  readOnly: boolean;
}

const AdminPanelPatientInfoForm: React.FC<IAdminPanelPatientInfoFormProps> = (
  props,
) => {
  const { data, loading: loadingProp = false, readOnly } = props;

  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { canEdit, setEdit } = useEdit();

  const { t } = useTranslation();

  const { loading, updatePatientInfo } = usePatientInfo({
    patientGuid: data.guid,
  });
  const { deviceType } = useUser();
  const { refetch } = usePatientDevices({
    patientGuid: data.guid,
    initialLoading: false,
  });

  const formik = useFormik({
    initialValues: data,
    onSubmit: async (values) => {
      await updatePatientInfo(values).then(() => {
        formik.resetForm({ values });
      });
      if (data.status !== values.status) {
        refetch();
      }
      setIsEdit(false);
      setEdit(false);
    },
    validationSchema: adminPanelPatientInfoValidationSchema(),
    validateOnChange: false,
  });

  const copyText = prepareArrToCopy([
    formik.values.loginInfo?.firstName,
    formik.values.loginInfo?.lastName,
    DateFormatter({
      date: formik.values.loginInfo?.birthDate,
      format: DateFormats['MM-dd-yyyy'],
    }),
    formik.values.guid,
    formik.values.loginInfo?.phone,
    formik.values.loginInfo?.email,
    formik.values.address,
    formik.values?.loginInfo?.city,
    formik.values?.loginInfo?.state,
    formik.values?.loginInfo?.zipPostalCode,
  ]);

  const patientTZ = timezonesDataSource().find(
    (tz) => tz.id === data.loginInfo?.timezone,
  )?.name;

  const onEdit = () => {
    setIsEdit(true);
    setEdit(true, formKey);
  };
  const onSave = () => {
    formik.submitForm();
  };
  const onCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEdit(false);
  };

  const selectBoxPlaceholder = isEdit ? undefined : '';

  useEffect(() => {
    formik.resetForm({ values: data });
  }, [data]);

  return (
    <PatientBlock
      title={t('labels.patientInfo')}
      isEdit={isEdit}
      onEdit={onEdit}
      onSave={onSave}
      onCancel={onCancel}
      canEdit={!readOnly}
      loading={loading || loadingProp}
      disabled={!canEdit(formKey)}
      copyText={copyText}
    >
      <BaseForm formik={formik} plaintext={!isEdit} readonly={!isEdit}>
        <BaseGrid>
          <BaseFormItemGroup
            className={s['border-bottom']}
            label={t('labels.personalInfo')}
          >
            <BaseGrid columns={2} className={s['inputs-wrapper']}>
              <BaseFormItem
                required
                name="loginInfo.title"
                label={t('labels.title')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <TitleSelectBox placeholder={selectBoxPlaceholder} />
              </BaseFormItem>
              <BaseFormItem
                name="loginInfo.firstName"
                label={t('labels.firstName')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseInput />
              </BaseFormItem>
              <BaseFormItem
                name="loginInfo.middleName"
                label={t('labels.middleName')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseInput />
              </BaseFormItem>
              <BaseFormItem
                name="loginInfo.lastName"
                label={t('labels.lastName')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseInput />
              </BaseFormItem>
              <BaseFormItem
                name="loginInfo.birthDate"
                label={t('labels.dob')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseDatepicker
                  maxDate={new Date()}
                  ageDOB={isEdit ? undefined : data.loginInfo?.birthDate}
                />
              </BaseFormItem>
              <BaseFormItem
                name="takingReadingTime"
                label={
                  <p className={s['taking-reading-label']}>
                    <span>{t('labels.timeForTakingReading')}</span>
                    <InformationTooltip
                      content={t('messages.timeAdjustedByPatientTZ', {
                        tz: patientTZ ?? '',
                      })}
                      trigger="hover"
                      closable={false}
                    />
                  </p>
                }
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseTimePicker
                  format={DateFormats['HH:mm']}
                  onCalendarChange={(date) => {
                    if (!date) formik.setFieldValue('takingReadingTime', null);
                  }}
                />
              </BaseFormItem>
            </BaseGrid>
          </BaseFormItemGroup>
          <BaseFormItemGroup
            label={t('labels.characteristics')}
            className={classNames({
              [s['border-bottom']]: isMobile(deviceType),
            })}
          >
            <BaseGrid columns={2} className={s['inputs-wrapper']}>
              <BaseFormItem
                name="gender"
                label={t('labels.gender')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <GenderSelectBox placeholder={selectBoxPlaceholder} />
              </BaseFormItem>
              <BaseFormItem
                name="loginInfo.language"
                label={t('labels.preferredLanguage')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <LanguageSelectBox placeholder={selectBoxPlaceholder} />
              </BaseFormItem>
              <BaseFormItem
                name="race"
                label={t('labels.race')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <RaceSelectBox
                  maxWidth={isMobile(deviceType) ? undefined : '260px'}
                  placeholder={selectBoxPlaceholder}
                />
              </BaseFormItem>
              <BaseFormItem
                name="ethnicity"
                label={t('labels.ethnicity')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <BaseInput />
              </BaseFormItem>
              <BaseFormItem
                name="martialStatus"
                label={t('labels.maritalStatus')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <MaritalStatusSelectBox placeholder={selectBoxPlaceholder} />
              </BaseFormItem>
              <BaseFormItem
                name="workingStatus"
                label={t('labels.workingStatus')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                <WorkingStatusSelectBox placeholder={selectBoxPlaceholder} />
              </BaseFormItem>

              <BaseGrid
                columns={isMobile(deviceType) && !isEdit ? 1 : 2}
                columnGap={20}
              >
                <BaseFormItem
                  name="height.ft"
                  label={t('labels.height')}
                  className={classNames({ [s['input-wrapper']]: !isEdit })}
                  showErrorBlock={isMobile(deviceType) ? isEdit : true}
                >
                  {isEdit ? (
                    <BaseInputNumber placeholder={isEdit ? 'ft′' : undefined} />
                  ) : (
                    <BaseInput
                      value={
                        formik.values.height
                          ? `${formik.values.height?.ft}′${formik.values.height?.in}″`
                          : undefined
                      }
                    />
                  )}
                </BaseFormItem>
                {isEdit && (
                  <BaseFormItem
                    name="height.in"
                    label="&nbsp;"
                    className={classNames({ [s['input-wrapper']]: !isEdit })}
                    showErrorBlock={isMobile(deviceType) ? isEdit : true}
                  >
                    <BaseInputNumber placeholder={isEdit ? 'in″' : undefined} />
                  </BaseFormItem>
                )}
              </BaseGrid>

              <BaseFormItem
                name="weightGoal"
                label={t('labels.weightGoal')}
                className={classNames({ [s['input-wrapper']]: !isEdit })}
                showErrorBlock={isMobile(deviceType) ? isEdit : true}
              >
                {isEdit ? (
                  <BaseInputNumber placeholder={isEdit ? 'lbs' : undefined} />
                ) : (
                  <BaseInput
                    value={
                      formik.values.weightGoal
                        ? `${formik.values.weightGoal} lbs`
                        : undefined
                    }
                  />
                )}
              </BaseFormItem>
            </BaseGrid>
          </BaseFormItemGroup>
        </BaseGrid>
      </BaseForm>
    </PatientBlock>
  );
};

export default AdminPanelPatientInfoForm;
